<template>
  <div class="pzdd">
    <!-- Store-wide KPI strip removed — it belonged to the old Intelligence tab;
         store-wide €/visitor + engagement live in Explore (store rollup), not in
         this per-zone view. -->

    <div v-if="!hasZones" class="pzdd-empty">
      <i class="bi bi-bullseye"></i>
      <div>
        <strong>{{ $t('zones.deepDive.emptyTitle') }}</strong>
        <span>{{ $t('zones.deepDive.emptyBody') }}</span>
      </div>
    </div>

    <div v-else class="pzdd-shell" :class="{ 'pzdd-shell--grid': browserMode === 'grid', 'pzdd-shell--bare': browserMode === 'none' }">
      <!-- Grid browser — a scannable card per zone across the top. -->
      <div v-if="browserMode === 'grid'" class="pzdd-grid">
        <button
          v-for="z in pickerZones"
          :key="z.zone"
          type="button"
          class="zone-card"
          :class="[`pat-${z.patternKind}`, { active: z.zone === selectedZone }]"
          @click="selectedZone = z.zone"
        >
          <div class="zc-head">
            <span class="zc-name">{{ z.zone }}</span>
            <span class="pat-pill" :style="{ background: z.patternBg, color: z.patternFg }">
              {{ z.patternIcon }} {{ z.patternChip }}
            </span>
          </div>
          <div class="zc-stats">
            <span><strong>{{ fmtPct(z.engagementRate) }}</strong> {{ $t('zones.deepDive.engage') }}</span>
            <span class="zc-sep">·</span>
            <span><strong>{{ fmtPct(z.conversionRate) }}</strong> {{ $t('zones.deepDive.buy') }}</span>
            <template v-if="z.potentialImpact > 0">
              <span class="zc-sep">·</span>
              <span class="zc-eur"><strong>{{ eur(z.potentialImpact) }}</strong> {{ $t('zones.deepDive.withinReach') }}</span>
            </template>
          </div>
        </button>
      </div>

      <!-- Left: zone picker (sidebar). browserMode 'none' shows the funnel only —
           selection comes from OUTSIDE (the Zones tab's role cards drive focusedZone). -->
      <aside v-else-if="browserMode === 'picker'" class="pzdd-picker">
        <div class="picker-label">{{ $t('zones.deepDive.pickerLabel') }}</div>

        <!-- Compact rich dropdown — shown only when the shell is stacked
             (≤1100px). Mirrors the list item (pill + zone + €) instead of a
             plain <select>, and closes on pick or outside-click. -->
        <div v-if="zone" class="pzdd-picker-dd">
          <button
            type="button"
            class="pzdd-dd-trigger"
            :class="`pat-${zone.patternKind}`"
            :aria-expanded="pickerOpen"
            @click="pickerOpen = !pickerOpen"
          >
            <span class="pat-pill dd-trigger-pill" :style="{ background: zone.patternBg, color: zone.patternFg }">
              <span class="pill-ico">{{ zone.patternIcon }}</span>
              <span class="pat-pill-text">{{ zone.patternChip }}</span>
            </span>
            <span class="pzdd-dd-zone">{{ zone.zone }}</span>
            <span v-if="zone.potentialImpact > 0" class="pzdd-dd-eur">{{ eur(zone.potentialImpact) }}</span>
            <i class="bi bi-chevron-down pzdd-dd-caret" :class="{ up: pickerOpen }"></i>
          </button>

          <div v-if="pickerOpen" class="pzdd-dd-backdrop" @click="pickerOpen = false"></div>
          <ul v-if="pickerOpen" class="pzdd-dd-menu" role="listbox">
            <li
              v-for="z in pickerZones"
              :key="z.zone"
              role="option"
              :aria-selected="z.zone === selectedZone"
              class="picker-item"
              :class="[`pat-${z.patternKind}`, { active: z.zone === selectedZone }]"
              @click="selectedZone = z.zone; pickerOpen = false"
            >
              <div class="picker-row">
                <span class="picker-zone">{{ z.zone }}</span>
                <span v-if="z.potentialImpact > 0" class="picker-eur">{{ eur(z.potentialImpact) }}</span>
              </div>
              <div class="picker-meta">
                <span class="pat-pill" :style="{ background: z.patternBg, color: z.patternFg }">
                  {{ z.patternIcon }} {{ z.patternChip }}
                </span>
              </div>
            </li>
          </ul>
        </div>

        <ul class="picker-list" role="listbox" :aria-activedescendant="`pzdd-zone-${selectedZone}`">
          <li
            v-for="z in pickerZones"
            :id="`pzdd-zone-${z.zone}`"
            :key="z.zone"
            role="option"
            :aria-selected="z.zone === selectedZone"
            class="picker-item"
            :class="[`pat-${z.patternKind}`, { active: z.zone === selectedZone }]"
            @click="selectedZone = z.zone"
          >
            <div class="picker-row">
              <span class="picker-zone">{{ z.zone }}</span>
              <span v-if="z.potentialImpact > 0" class="picker-eur">{{ eur(z.potentialImpact) }}</span>
            </div>
            <div class="picker-meta">
              <span class="pat-pill" :style="{ background: z.patternBg, color: z.patternFg }">
                {{ z.patternIcon }} {{ z.patternChip }}
              </span>
            </div>
          </li>
        </ul>
      </aside>

      <!-- Right: zone story -->
      <section v-if="zone" class="pzdd-zone">
        <header class="zone-head">
          <h3>{{ zone.zone }}</h3>
          <span class="pat-pill big" :style="{ background: zone.patternBg, color: zone.patternFg }">
            {{ zone.patternIcon }} {{ zone.patternChip }}
          </span>
          <!-- Consolidated within-reach + lever phrasing. The lever noun
               keeps the lever-eng / lever-conv color so the at-a-glance
               visual signal survives without needing a second pill. -->
          <!-- ONE tooltip on the whole within-reach line. The inner
               lever-clause tooltip was redundant: hovering it triggered
               BOTH the outer atStakeTooltip AND the inner one, stacking
               two bubbles on top of each other. atStakeTooltip already
               explains the math + the lever. -->
          <Tooltip v-if="!isTripwireOnly(zone.zone) && zone.potentialImpact > 0" :text="atStakeTooltip">
            <span class="zone-stake">
              <strong>{{ eur(zone.potentialImpact) }}</strong> {{ $t('zones.deepDive.withinReach') }}
              <span v-if="zone.leverKind" class="lever-clause" :class="`lever-${zone.leverKind}`">· {{ leverNoun(zone.leverKind) }}</span>
              <i class="bi bi-info-circle stake-info"></i>
            </span>
          </Tooltip>
          <Tooltip v-else-if="!isTripwireOnly(zone.zone) && !zone.avgValue" :text="$t('zones.deepDive.noValueTip')">
            <span class="zone-stake muted">
              <i class="bi bi-exclamation-circle"></i>
              {{ $t('zones.deepDive.noValue') }}
            </span>
          </Tooltip>
        </header>

        <!-- Per-zone dwell KPI for tripwire-only zones — the only zone-level
             measurement we have, since polygon-tracked engagement isn't
             available. Mirrors the chip styling of the funnel/header. -->
        <div
          v-if="isTripwireOnly(zone.zone) && props.tripwireDwellMap?.[zone.zone]?.avgSec > 0"
          class="zone-dwell-callout"
        >
          <i class="bi bi-hourglass-split"></i>
          <span class="dwell-callout-label">{{ $t('zones.deepDive.dwellLabel') }}</span>
          <span class="dwell-callout-value">{{ fmtDwellSec(props.tripwireDwellMap[zone.zone].avgSec) }}</span>
          <span
            v-if="props.tripwireDwellMap[zone.zone]?.confidence"
            :class="['conf-chip', `conf-${props.tripwireDwellMap[zone.zone].confidence}`]"
          >{{ confidenceText(props.tripwireDwellMap[zone.zone].confidence) }}</span>
        </div>

        <!-- Funnel -->
        <div class="zone-funnel">
          <div class="funnel-title">
            <i class="bi bi-funnel"></i>
            <span>{{ $t('zones.deepDive.funnel.title') }}</span>
          </div>
          <div
            v-for="(s, i) in funnelStages"
            :key="s.key"
            class="funnel-row"
          >
            <div class="funnel-row-head">
              <span class="funnel-label">{{ s.label }}</span>
              <span class="funnel-count">{{ fmt(s.count) }}</span>
            </div>
            <div class="funnel-bar-wrap">
              <div
                class="funnel-bar"
                :style="{ width: s.pctOfStore + '%', background: s.color }"
              ></div>
              <!-- Funnel % label — the visible text already says
                   "X.X% of <prev stage>" so the tooltip ("X% of store
                   visitors") only adds a different denominator. Dropped
                   to avoid bubble clipping inside .funnel-bar-wrap's
                   overflow:hidden + reduce hover noise. -->
              <span class="funnel-pct">
                <template v-if="i === 0">{{ pct1(100, 0) }}</template>
                <template v-else>
                  {{ pct1(s.pctOfPrev) }}
                  <small class="funnel-pct-sub">{{ $t(`zones.deepDive.funnel.ofPrev.${s.key}`) }}</small>
                </template>
              </span>
            </div>
            <div v-if="i > 0 && s.dropPct > 0" class="funnel-drop">
              {{ $t('zones.deepDive.funnel.drop', { pct: pct1(s.dropPct), n: fmt(s.dropCount), who: s.prevLabelShort }) }}
            </div>
          </div>
        </div>

        <!-- Sensitivity — one block per lever, stepped in +pp up to best-in-class.
             Each card: the +pp move, the resulting %, and the extra €/mo (real funnel
             math, so the levers differ correctly). Last card = best-in-class; a lever
             already there still shows a +1pp "beyond" card. -->
        <div v-if="!isTripwireOnly(zone.zone) && zone.avgValue && sensitivityBlocks.length" class="zone-sens">
          <div class="sens-head">
            <i class="bi bi-graph-up-arrow"></i>
            <span>{{ $t('zones.deepDive.sens.head') }}</span>
          </div>
          <div class="sens-blocks">
            <div v-for="b in sensitivityBlocks" :key="b.key" class="sens-block" :class="'lev-' + b.key">
              <div class="sens-block-head">
                <span class="sens-block-lever">{{ b.label }}</span>
                <span class="sens-block-sub">{{ b.sub }}</span>
              </div>
              <div class="sens-steps">
                <div v-for="(s, i) in b.steps" :key="i" class="sens-card" :class="{ bic: s.isBic || s.atBic, beyond: s.beyond }">
                  <template v-if="s.atBic">
                    <span class="sens-step"><i class="bi bi-check2-circle"></i> {{ $t('zones.deepDive.sens.bic') }}</span>
                    <span class="sens-rate">{{ $t('zones.deepDive.sens.youreHere', { rate: s.rateText }) }}</span>
                  </template>
                  <template v-else>
                    <span class="sens-step">
                      {{ ppText(s.pp) }}
                      <span v-if="s.isBic" class="sens-tag">{{ $t('zones.deepDive.sens.tagBic') }}</span>
                      <span v-else-if="s.beyond" class="sens-tag beyond">{{ $t('zones.deepDive.sens.tagBeyond') }}</span>
                    </span>
                    <span class="sens-rate">→ {{ s.rateText }}</span>
                    <span class="sens-eur">+{{ eur(s.eur) }}</span>
                  </template>
                </div>
              </div>
            </div>
          </div>
          <p class="sens-note">
            <i18n-t keypath="zones.deepDive.sens.note" scope="global">
              <template #one><strong>{{ $t('zones.deepDive.sens.one') }}</strong></template>
            </i18n-t>
          </p>
        </div>
        <div v-else-if="!isTripwireOnly(zone.zone) && !zone.avgValue" class="sim-disabled-note">
          <i class="bi bi-exclamation-circle"></i>
          <i18n-t keypath="zones.deepDive.sens.noValue" scope="global">
            <template #field><strong>{{ $t('zones.deepDive.sens.field') }}</strong></template>
          </i18n-t>
        </div>

        <!-- Insights & actions
             Strong-pattern zones (engagement + conversion both near the
             segment ceilings) don't have a meaningful "next move". Show a
             dedicated empty state instead of the regular Why/Try block so
             we don't surface a fake lever, a -100% trend driven by zeroed
             potential, or out-of-context tune-up bullets. -->
        <!-- Tripwire-only zones: no "why and what to try" panel at all.
             Both the lever recommendations and the engagement/conversion
             chips it would carry depend on polygon-tracked measurements
             we don't have. -->
        <div v-if="isTripwireOnly(zone.zone)" class="zone-insights">
          <div class="ins-head">
            <i class="bi bi-hourglass-split"></i>
            <span>{{ $t('zones.deepDive.tripwire.head') }}</span>
          </div>
          <div class="ins-chips">
            <span class="ins-chip subtle">{{ $t('zones.deepDive.tripwire.reached', { n: fmt(zone.visitors) }) }}</span>
            <span class="ins-chip subtle">{{ $t('zones.deepDive.tripwire.type') }}</span>
          </div>
        </div>
        <div v-else-if="zone.patternKind === 'strong'" class="zone-insights zone-insights-strong">
          <div class="ins-head">
            <i class="bi bi-trophy"></i>
            <span>{{ $t('zones.deepDive.strong.head') }}</span>
          </div>
          <div class="ins-chips">
            <span class="ins-chip subtle">{{ $t('zones.deepDive.strong.visitors', { n: fmt(zone.visitors) }) }}</span>
            <span class="ins-chip subtle">{{ $t('zones.deepDive.strong.engagement', { pct: fmtPct(zone.engagementRate) }) }}</span>
            <span class="ins-chip subtle">{{ $t('zones.deepDive.strong.conversion', { pct: fmtPct(zone.conversionRate) }) }}</span>
            <span v-if="zone.trendPct != null" class="ins-chip" :class="`trend-${zone.trendClass}`">
              {{ trendText(zone.trendPct) }}
            </span>
          </div>
          <p class="ins-changed">
            <i class="bi bi-shield-check"></i>
            <i18n-t keypath="zones.deepDive.strong.body" tag="span" scope="global">
              <template #emax>{{ fmtPct(emax) }}</template>
              <template #cmax>{{ fmtPct(cmax) }}</template>
              <template #protect><strong>{{ $t('zones.deepDive.strong.protect') }}</strong></template>
            </i18n-t>
          </p>
        </div>

        <!-- "Why it moved" block removed — it restated period-over-period momentum
             (which Today's "Allen flagged" + Data/Explore own), duplicated the
             funnel/header/sensitivity metrics, and its suggestions belong in Actions.
             The zone's "why" is the funnel + sensitivity + strategic role. -->


      </section>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import Tooltip from '@/components/ui/Tooltip.vue'
import { CEILINGS_BY_SEGMENT, PATTERN_META } from '@/prioritization/settings'
import { useDetectionZoneStore } from '@/stores/DetectionZone'
import { useI18n } from 'vue-i18n'
import { currentLanguage } from '@/i18n'
import { formatNumber, formatEuro, formatPercent, formatPercentPoints, formatDuration } from '@/i18n/format'

const { t, te } = useI18n()
// English keeps its own formats exactly as before; Dutch goes through format.js.
const isEn = () => currentLanguage() === 'en'
const confidenceText = (c) => (te(`zones.deepDive.confidence.${c}`) ? t(`zones.deepDive.confidence.${c}`) : `${c} confidence`)

const detectionZoneStore = useDetectionZoneStore()

// Map of zone name → true for zones with tripwires and no polygon. The
// Intelligence tab's funnel/verdict logic depends on polygon-derived
// visit durations (clear-lingerer counts); for tripwire-only zones
// those don't exist, so the calculation defaults to 0 and we render
// misleading "100% drop-off / Awareness gap" surfaces. We instead
// suppress those rows and emit a neutral verdict pill.
const tripwireOnlyMap = computed(() => {
    const map = new Map()
    const zones = detectionZoneStore.detectionzones || []
    for (const z of zones) {
        const name = z.DetectionZoneName || z.name
        if (!name) continue
        const hasPolygon = !!z.DetectionZonePointArray
        const hasTripwire = !!z.DetectionZoneTripwirePoints
        if (hasTripwire && !hasPolygon) map.set(name, true)
    }
    return map
})
const isTripwireOnly = (zoneName) =>
    !!zoneName && tripwireOnlyMap.value.get(zoneName) === true

// Per-zone dwell for the callout on tripwire-only zones.
function fmtDwellSec(s) {
    if (s == null || !Number.isFinite(+s)) return ''
    if (!isEn()) return formatDuration(+s)
    const n = Math.round(+s)
    if (n < 60) return `${n}s`
    const m = Math.floor(n / 60)
    const r = n - m * 60
    if (m < 60) return r ? `${m}m ${r}s` : `${m}m`
    const h = Math.floor(m / 60)
    return `${h}h ${m - h * 60}m`
}

const props = defineProps({
  // Authoritative ranked list from computePriorities()
  priorities: { type: Array, default: () => [] },
  storeTotalVisitors: { type: Number, default: 0 },
  segmentKey: { type: String, default: 'default' },
  // External focus (e.g., the zone the Today hero linked to). When set,
  // the picker auto-selects it on mount / on change.
  focusedZone: { type: String, default: '' },
  // Per-zone tripwire dwell data — { zoneName: { avgSec, confidence } }.
  // Surfaces a per-zone dwell KPI on tripwire-only zones (which have no
  // polygon visit durations and therefore none of the engagement / "within
  // reach" KPIs the funnel would otherwise show).
  tripwireDwellMap: { type: Object, default: () => ({}) },
  // Zone-browser layout: 'picker' = left sidebar list (default), 'grid' = a
  // scannable card grid across the top. Both drive the same per-zone story below.
  browserMode: { type: String, default: 'picker' }
})

const ceilings = computed(() =>
  CEILINGS_BY_SEGMENT[props.segmentKey] || CEILINGS_BY_SEGMENT.default
)
const emax = computed(() => ceilings.value.Emax)
const cmax = computed(() => ceilings.value.Cmax)

const atStakeTooltip = computed(() => t('zones.deepDive.atStakeTip'))

// "What changed" microcopy: when we have previous-period data for this zone,
// pick the largest single driver of the trend and surface it in plain English.
// Picks the largest absolute relative move (visitors / engagement / conversion);
// avgValue moves are usually config edits, so we mention them only as a hint.
const whatChanged = computed(() => {
  const z = zone.value
  if (!z) return null
  if (z.prevVisitors == null) return null   // no compare-to data
  if (z.trendPct == null) return null

  const drivers = []
  if (z.prevVisitors > 0) {
    const dV = z.visitors - z.prevVisitors
    const rel = (dV / z.prevVisitors) * 100
    if (Math.abs(rel) >= 3) {
      drivers.push({
        kind: 'visitors',
        rel,
        text: `traffic ${dV >= 0 ? 'rose' : 'fell'} ${Math.abs(Math.round(rel))}% (${fmt(Math.abs(dV))} ${dV >= 0 ? 'more' : 'fewer'} visitors reached this zone)`,
        weight: Math.abs(rel)
      })
    }
  }
  if (z.prevEngagementRate >= 0) {
    const dPP = (z.engagementRate - z.prevEngagementRate) * 100
    if (Math.abs(dPP) >= 0.5) {
      drivers.push({
        kind: 'engagement',
        rel: dPP,
        text: `engagement ${dPP >= 0 ? 'rose' : 'fell'} ${Math.abs(dPP).toFixed(1)}pp (${fmtPctRaw(z.prevEngagementRate)} → ${fmtPctRaw(z.engagementRate)})`,
        weight: Math.abs(dPP) * 5
      })
    }
  }
  if (z.prevConversionRate >= 0) {
    const dPP = (z.conversionRate - z.prevConversionRate) * 100
    if (Math.abs(dPP) >= 0.25) {
      drivers.push({
        kind: 'conversion',
        rel: dPP,
        text: `conversion assumption changed by ${dPP >= 0 ? '+' : ''}${dPP.toFixed(1)}pp`,
        weight: Math.abs(dPP) * 8
      })
    }
  }

  if (!drivers.length) return null
  drivers.sort((a, b) => b.weight - a.weight)
  return drivers[0].text
})

// Emotional framing of the engagement gap: how many people walked past without
// engaging. This re-uses the funnel data — "reached − engaged" is how many
// visitors entered the zone but didn't become a clear lingerer.
// (Removed the "walked on without engaging" narrative line — it was hardcoded to
// the engagement step (misleading for conversion-bottlenecked zones) and duplicated
// the funnel's own reached→engaged drop-off.)

// Decorate priorities with PATTERN_META so chips render with consistent colors.
// Tripwire-only zones get a neutral pill — the engagement-driven patterns
// (awareness gap, conversion bottleneck, etc.) are computed off a polygon
// measurement we don't have for them, so any verdict would be a guess.
const pickerZones = computed(() =>
  (props.priorities || []).map(p => {
    if (isTripwireOnly(p.zone)) {
      return {
        ...p,
        patternChip: t('zones.deepDive.dwellChip'),
        patternIcon: '⌛',
        patternFg: '#37526b',
        patternBg: '#eef3f8'
      }
    }
    const kind = PATTERN_META[p.patternKind] ? p.patternKind : 'neutral'
    const meta = PATTERN_META[kind]
    return {
      ...p,
      patternChip: isEn() ? meta.chip : t(`focus.pattern.${kind}.chip`),
      patternIcon: meta.icon,
      patternFg: meta.color,
      patternBg: meta.bg
    }
  })
)

const hasZones = computed(() => pickerZones.value.length > 0)

// Stacked-layout zone dropdown open/close state.
const pickerOpen = ref(false)

// Selected zone — defaults to focusedZone (if present in list), else first.
const selectedZone = ref('')
function pickInitialZone() {
  const list = pickerZones.value
  if (!list.length) { selectedZone.value = ''; return }
  if (props.focusedZone && list.find(z => z.zone === props.focusedZone)) {
    selectedZone.value = props.focusedZone
    return
  }
  selectedZone.value = list[0].zone
}
watch(() => [pickerZones.value.map(z => z.zone).join('|'), props.focusedZone], pickInitialZone, { immediate: true })

const zone = computed(() =>
  pickerZones.value.find(z => z.zone === selectedZone.value) || null
)

// ---------- Funnel rows ----------
// Each stage carries: absolute count, % of store (bar width), % of previous stage
// (the meaningful conversion-at-this-step), and drop-off vs the previous stage.
const funnelStages = computed(() => {
  if (!zone.value) return []
  const total = Math.max(1, props.storeTotalVisitors || 0)
  const v = zone.value.visitors || 0                                  // reached
  const eng = Math.round(v * (zone.value.engagementRate || 0))        // engaged
  const conv = Math.round(eng * (zone.value.conversionRate || 0))     // converted

  const simEng = Math.round(v * simEngagementRate.value)
  const simConv = Math.round(simEng * simConversionRate.value)

  const twOnly = isTripwireOnly(zone.value.zone)
  const stages = [
    { key: 'store', label: t('zones.deepDive.funnel.store'), shortLabel: 'store visitors',  count: total, color: '#36910d', sim: total },
    { key: 'reach', label: t('zones.deepDive.funnel.reach'), shortLabel: t('zones.deepDive.funnel.notReach'), count: v,    color: '#4F7EB3', sim: v },
  ]
  // Tripwire-only zones stop at "reached" — we can't measure engaged
  // or converted without polygon-tracked visit durations. Adding the
  // rows with hard zeros would (a) make the funnel look like a
  // verdict, and (b) drive misleading drop-off lines and prioritization.
  if (!twOnly) {
    stages.push(
      { key: 'eng',   label: t('zones.deepDive.funnel.eng'), shortLabel: t('zones.deepDive.funnel.notEng'), count: eng, color: '#62B5D9', sim: simEng },
      { key: 'conv',  label: zone.value.conversionMeasured ? t('zones.deepDive.funnel.convMeasured') : t('zones.deepDive.funnel.convEstimated'), shortLabel: t('zones.deepDive.funnel.notConv'), count: conv, color: '#0D9488', sim: simConv }
    )
  }

  let prev = null
  return stages.map((s, i) => {
    const pctOfStore = Math.min(100, (s.count / total) * 100)
    const simPctOfStore = i >= 2 ? Math.min(100, (s.sim / total) * 100) : null

    // % of the *previous* stage that continued (the conversion at this step).
    const pctOfPrev = i === 0 || !prev || prev.count <= 0
      ? 100
      : Math.min(100, (s.count / prev.count) * 100)

    const dropPct = i > 0 && prev && prev.count > 0
      ? Math.max(0, 100 - pctOfPrev)
      : 0
    const dropCount = i > 0 && prev ? Math.max(0, prev.count - s.count) : 0

    const row = {
      ...s,
      pctOfStore,
      simPctOfStore,
      pctOfPrev,
      dropPct,
      dropCount,
      prevLabel: prev ? prev.label : '',
      prevLabelShort: i > 0 ? s.shortLabel : ''
    }
    prev = { count: s.count, label: s.label }
    return row
  })
})

// ---------- Scenario baselines (used by funnel sim bars + sensitivity math) ----------
const engagementUplift = ref(0)   // percentage points
const conversionUplift = ref(0)   // percentage points

// Reset uplifts whenever the zone, its baseline rates, or the period change —
// otherwise the slider value sits on top of a stale baseline and misleads.
watch(
  () => zone.value && [
    zone.value.zone,
    zone.value.engagementRate,
    zone.value.conversionRate,
    zone.value.avgValue,
    zone.value.visitors
  ].join('|'),
  () => {
    engagementUplift.value = 0
    conversionUplift.value = 0
  }
)

// Simulator slider range — capped at 100% (mathematical absolute),
// NOT at the sector ceiling. A store currently at or near sector
// best practice should still be able to model "what if we exceeded
// the industry benchmark?" scenarios. The ceiling is a reference
// line on the Pattern Matrix, not a hard wall on the simulator.
const maxEngagementUplift = computed(() => {
  if (!zone.value) return 0
  return Math.max(0, (1 - (zone.value.engagementRate || 0)) * 100)
})
const maxConversionUplift = computed(() => {
  if (!zone.value) return 0
  return Math.max(0, (1 - (zone.value.conversionRate || 0)) * 100)
})

const simEngagementRate = computed(() => {
  if (!zone.value) return 0
  return Math.min(1, (zone.value.engagementRate || 0) + engagementUplift.value / 100)
})
const simConversionRate = computed(() => {
  if (!zone.value) return 0
  return Math.min(1, (zone.value.conversionRate || 0) + conversionUplift.value / 100)
})

const currentRealizedEur = computed(() => {
  if (!zone.value) return 0
  return (zone.value.visitors || 0)
    * (zone.value.engagementRate || 0)
    * (zone.value.conversionRate || 0)
    * (zone.value.avgValue || 0)
})

const simRealizedEur = computed(() => {
  if (!zone.value) return 0
  return (zone.value.visitors || 0)
    * simEngagementRate.value
    * simConversionRate.value
    * (zone.value.avgValue || 0)
})

const simDeltaEur = computed(() =>
  Math.max(0, simRealizedEur.value - currentRealizedEur.value)
)

const hasUplift = computed(() =>
  engagementUplift.value > 0 || conversionUplift.value > 0
)
function resetSim() {
  engagementUplift.value = 0
  conversionUplift.value = 0
}

// ---------- Sensitivity analysis (fixed +pp blocks up to best-in-class) ----------
// One block per lever. Steps are absolute percentage-point moves on THAT lever's
// rate (+1 / +5 / +10 / +15 / +20 …), each shown with the resulting % and the extra
// €/mo from the real funnel (engagement lifts reached×conv; conversion lifts engaged;
// reach lifts everyone — so they differ correctly). The last block is best-in-class
// (the exact +Xpp to the ceiling); levers with more headroom get more blocks. A
// lever already at/above best-in-class still shows a +1pp "beyond" block (always room).
const PP_STEPS = [1, 5, 10, 15, 20]
const ppFmt = (pp) => (Math.abs(pp % 1) < 0.05 ? pp.toFixed(0) : pp.toFixed(1))
function buildLeverSteps(current, ceiling, eurPerUnit, rateFmt) {
  const gapPp = (ceiling - current) * 100
  const steps = []
  if (gapPp > 0.5) {
    for (const pp of PP_STEPS) {
      if (pp >= gapPp - 0.05) break
      steps.push({ pp, rateText: rateFmt(current + pp / 100), eur: eurPerUnit * (pp / 100), isBic: false })
    }
    steps.push({ pp: gapPp, rateText: rateFmt(ceiling), eur: eurPerUnit * (gapPp / 100), isBic: true })
  } else {
    // already at/above best-in-class — show the achieved marker FIRST, then the
    // +1pp "beyond" block (there's always more headroom).
    steps.push({ pp: 0, rateText: rateFmt(current), eur: 0, atBic: true })
    steps.push({ pp: 1, rateText: rateFmt(current + 0.01), eur: eurPerUnit * 0.01, beyond: true })
  }
  return steps
}
const sensitivityBlocks = computed(() => {
  const z = zone.value
  if (!z || !z.avgValue || isTripwireOnly(z.zone)) return []
  const V = z.visitors || 0
  const E = z.engagementRate || 0
  const C = z.conversionRate || 0
  const A = z.avgValue || 0
  const store = props.storeTotalVisitors || 0
  // Normalise the per-pp € to a 30-day month, matching potentialImpact (the
  // recompute below rebuilds € from raw period visitors, so it needs the same
  // factor computePriorities stamped on the row). Absent → 1 (already 30-day).
  const scale = z.periodScale || 1
  const pctFmt = (r) => pct1(r * 100)
  const out = []
  // Reach = % of store footfall reaching this zone (rate = V/store, ceiling 100%).
  if (store > 0 && V > 0) {
    out.push({ key: 'reach', label: t('zones.deepDive.sens.reach'), sub: t('zones.deepDive.sens.reachSub'),
      steps: buildLeverSteps(V / store, 1.0, E * C * A * store * scale, pctFmt) })
  }
  out.push({ key: 'eng', label: t('zones.deepDive.sens.eng'), sub: t('zones.deepDive.sens.engSub'),
    steps: buildLeverSteps(E, emax.value, V * C * A * scale, pctFmt) })
  out.push({ key: 'conv', label: t('zones.deepDive.sens.conv'), sub: t('zones.deepDive.sens.convSub'),
    steps: buildLeverSteps(C, cmax.value, V * E * A * scale, pctFmt) })
  return out
})

// ---------- Strip KPIs (engagement % + €/visitor across all product zones) ----------
const totalVisitors = computed(() =>
  (props.priorities || []).reduce((acc, z) => acc + (z.visitors || 0), 0)
)
const totalEngaged = computed(() =>
  (props.priorities || []).reduce((acc, z) => acc + Math.round((z.visitors || 0) * (z.engagementRate || 0)), 0)
)
const totalRealized = computed(() =>
  (props.priorities || []).reduce(
    (acc, z) => acc + (z.visitors || 0) * (z.engagementRate || 0) * (z.conversionRate || 0) * (z.avgValue || 0),
    0
  )
)
const ecvPerVisitor = computed(() => {
  const v = props.storeTotalVisitors || totalVisitors.value || 0
  return v > 0 ? (totalRealized.value || 0) / v : 0
})
const engagedPctText = computed(() => {
  const v = totalVisitors.value || 0
  if (v <= 0) return '—'
  return `${((totalEngaged.value / v) * 100).toFixed(1)}%`
})

// ---------- Formatters ----------
function fmt(n){ return isEn() ? Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 }) : formatNumber(Number(n || 0)) }
function fmtMoney(n){ return Math.round(Number(n || 0)).toLocaleString(undefined, { maximumFractionDigits: 0 }) }
// "€1,234" in English (as before); "€ 1.234" in Dutch.
function eur(n){ return isEn() ? `€${fmtMoney(n)}` : formatEuro(Math.round(Number(n || 0))) }
function fmtMoney2(n){ return Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }
function fmtPct(rate){ return isEn() ? `${(Number(rate || 0) * 100).toFixed(1)}%` : formatPercent(Number(rate || 0) * 100, { decimals: 1 }) }
// A value already in percent: "12.5%" / "12,5%".
function pct1(v, decimals = 1){ return isEn() ? (decimals ? `${Number(v).toFixed(decimals)}%` : `${v}%`) : formatPercent(v, { decimals }) }
// The +pp step on a sensitivity card: "+5pp" / "+5 %-punt".
function ppText(pp){
  if (isEn()) return `+${ppFmt(pp)}pp`
  return formatPercentPoints(pp, { decimals: Math.abs(pp % 1) < 0.05 ? 0 : 1 })
}
function trendText(p){ return isEn() ? `Trend: ${p}%` : t('zones.deepDive.strong.trend', { pct: String(p).replace('.', ',') }) }
function fmtPctRaw(rate){ return `${(Number(rate || 0) * 100).toFixed(1)}%` }

// Helpers for the consolidated within-reach + lever sentence (mirrors
// TodayHero). classifier.computePotentialEUR encodes the pp value into
// leverShort (e.g. "Engagement +5pp"); pulling the number back out keeps
// the sentence in sync with the underlying math.
function leverPp(leverShort){
  if (!leverShort) return ''
  const m = String(leverShort).match(/\+?(\d+(?:\.\d+)?)pp/i)
  return m ? m[1] : ''
}
function leverNoun(leverKind){
  if (['eng', 'conv', 'reach'].includes(leverKind)) return t(`zones.deepDive.lever.${leverKind}`)
  return ''
}
</script>

<style scoped>
.pzdd{
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 4px 0 24px;
}

/* ---------- Top strip ---------- */
.pzdd-strip{
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(220px, 100%), 1fr));
  gap: 16px;
}
.strip-kpi{
  background: #fff;
  border: 1px solid #e6edf3;
  border-left: 4px solid #36910d;
  border-radius: 12px;
  padding: 14px 18px;
  box-shadow: 0 3px 12px rgba(13,38,76,.06);
}
.strip-label{ color:#6b7a86; font-size:.85rem; font-weight:600; }
.strip-value{ color:#1a2a36; font-size:1.7rem; font-weight:700; line-height:1.15; margin-top:4px; }
.strip-meta{ color:#6b7a86; font-size:.78rem; margin-top:4px; }

/* ---------- Empty ---------- */
.pzdd-empty{
  display:flex; align-items:center; gap:14px;
  padding:18px 22px;
  border:1px dashed #d3dbe1;
  border-radius:12px;
  background:#fff;
  color:#425461;
}
.pzdd-empty i{ font-size:1.6rem; color:#36910d; }
.pzdd-empty strong{ display:block; color:#1a2a36; }
.pzdd-empty span{ color:#6b7a86; font-size:.9rem; }

/* ---------- Two-column shell ---------- */
.pzdd-shell{
  display: grid;
  /* minmax(0,1fr), not 1fr: a bare 1fr track is minmax(auto,1fr), whose `auto`
     minimum is the column's content min-content — so the main column refused to
     shrink and forced the whole component (and the page content column) wider
     than the viewport, clipping the Intelligence-tab charts. minmax(0,1fr) lets
     the track shrink to 0; min-width:0 lets the content inside it shrink too. */
  grid-template-columns: 280px minmax(0, 1fr);
  gap: 24px;
}
.pzdd-shell > *{ min-width: 0; }

/* Grid browser mode — single column: card grid on top, story below. */
.pzdd-shell--grid{ grid-template-columns: 1fr; }
/* Bare mode — funnel only (selection lives outside, in the Zones role cards). */
.pzdd-shell--bare{ grid-template-columns: 1fr; }
.pzdd-grid{
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(264px, 1fr));
  gap: 12px;
  margin-bottom: 6px;
}
.zone-card{
  text-align: left; cursor: pointer;
  background: #fff; border: 1px solid #e8eef7; border-left: 3px solid #cbd6e6;
  border-radius: 12px; padding: 11px 13px;
  display: flex; flex-direction: column; gap: 8px;
  transition: box-shadow .15s ease, transform .15s ease, border-color .15s ease;
}
.zone-card:hover{ box-shadow: 0 6px 16px rgba(13,38,76,.08); transform: translateY(-1px); }
.zone-card.active{ border-left-color: #4F7EB3; box-shadow: 0 0 0 2px #cfe0f3 inset; }
.zc-head{ display: flex; align-items: flex-start; justify-content: space-between; gap: 8px; }
.zc-name{ font-weight: 700; color: #2b3a46; font-size: 13.5px; min-width: 0; line-height: 1.25; overflow-wrap: break-word; }
.zc-head .pat-pill{ flex: 0 0 auto; }
.zc-stats{ font-size: 11.5px; color: #6b7a87; display: flex; flex-wrap: wrap; align-items: baseline; gap: 5px; }
.zc-stats strong{ color: #2e3b46; font-variant-numeric: tabular-nums; }
.zc-sep{ color: #c4ccd6; }
.zc-eur strong{ color: #2c6f58; }

/* Traffic sensitivity — folded in from the retired Prioritization tab. */
.zone-traffic{ margin-top: 18px; padding-top: 14px; border-top: 1px solid #eef2f7; }
.zt-head{ display: flex; align-items: baseline; gap: 8px; flex-wrap: wrap; margin-bottom: 9px; }
.zt-title{ font-size: 13px; font-weight: 700; color: #2b3a46; display: inline-flex; align-items: center; gap: 6px; }
.zt-title .bi{ color: #4F7EB3; }
.zt-hint{ font-size: 11px; font-style: italic; color: #9aa7b3; }
.zt-rows{ display: flex; gap: 8px; flex-wrap: wrap; }
.zt-row{
  display: flex; flex-direction: column; gap: 2px;
  background: #f7f9fc; border: 1px solid #e8eef7; border-radius: 10px;
  padding: 8px 13px; min-width: 100px;
}
.zt-lbl{ font-size: 11px; color: #6b7a87; }
.zt-val{ font-size: 15px; font-weight: 800; color: #4F7EB3; font-variant-numeric: tabular-nums; }
.zt-share{ margin-top: 9px; font-size: 11.5px; color: #7a8895; }
.zt-share strong{ color: #2e3b46; }

/* ---------- Sensitivity analysis (per-lever fixed-step blocks) ---------- */
.zone-sens{
  background:#fff; border:1px solid #e6edf3; border-radius:12px;
  padding:18px 20px; box-shadow:0 3px 12px rgba(13,38,76,.06);
  display:flex; flex-direction:column; gap:16px;
}
.sens-head{ display:flex; align-items:center; gap:8px; font-weight:700; color:#1a2a36; }
.sens-head i{ color:#4F7EB3; }
.sens-blocks{ display:flex; flex-direction:column; gap:16px; }
.sens-block{ display:flex; flex-direction:column; gap:8px; padding-left:11px; border-left:3px solid #cbd6e6; }
.sens-block.lev-reach{ border-left-color:#3E8C80; }
.sens-block.lev-eng{   border-left-color:#4F7EB3; }
.sens-block.lev-conv{  border-left-color:#B2C149; }
.sens-block-head{ display:flex; align-items:baseline; gap:8px; flex-wrap:wrap; }
.sens-block-lever{ font-weight:700; color:#2b3a46; font-size:.92rem; }
.sens-block-sub{ font-size:.78rem; color:#9aa7b3; }
.sens-steps{ display:flex; gap:8px; flex-wrap:wrap; }
.sens-card{
  position:relative; display:flex; flex-direction:column; gap:1px;
  background:#f7f9fc; border:1px solid #e8eef7; border-radius:10px;
  padding:7px 13px; min-width:104px;
}
.sens-card.bic{ background:#f4faf6; border-color:#cfe8d2; }
.sens-card.bic .sens-step{ color:#2f6f3a; }
.sens-card.bic .sens-step i{ color:#2c9c4a; }
.sens-card.beyond{ background:#fff8ec; border-color:#ffe1a8; }
.sens-step{
  font-size:11.5px; color:#2b3a46; font-weight:700;
  display:flex; align-items:center; gap:5px; flex-wrap:wrap;
}
.sens-tag{
  font-size:8.5px; text-transform:uppercase; letter-spacing:.3px; font-weight:700;
  color:#2f6f3a; background:#e3f3e7; border-radius:6px; padding:1px 5px;
}
.sens-tag.beyond{ color:#9a6b00; background:#fff3d6; }
.sens-rate{ font-size:11px; color:#7a8895; }
.sens-eur{ font-size:14.5px; font-weight:800; color:#2c6f58; font-variant-numeric:tabular-nums; margin-top:2px; }
.sens-note{ margin:0; font-size:.78rem; color:#7a8895; font-style:italic; }
.sens-note strong{ color:#5a6b78; }
/* Stack at 1100 (not 900): below this the right column is squeezed too narrow
   for the funnel + simulator, so give the deep-dive full width sooner. When
   stacked, the picker sits ABOVE the deep-dive — sticky there would pin the
   list over the content as you scroll, so drop sticky in the stacked layout. */
@media (max-width: 1100px){
  .pzdd-shell{ grid-template-columns: 1fr; }
  /* 2-class selector so it actually beats the later base `.pzdd-picker{position:sticky}`
     rule. Sticky here was creating a stacking context that trapped the open
     dropdown menu BELOW the deep-dive (funnel bars / within-reach pill). */
  .pzdd-shell .pzdd-picker{ position: static; top: auto; }
  /* swap the vertical list for the compact rich dropdown when stacked.
     Both selectors are 2-class so they outrank the single-class base rules
     regardless of source order (media queries add no specificity). */
  .pzdd-picker .picker-list{ display: none; }
  .pzdd-picker .pzdd-picker-dd{ display: block; }
}

/* ---------- Picker ---------- */
.pzdd-picker{
  background:#fff;
  border:1px solid #e6edf3;
  border-radius:12px;
  padding:12px;
  height: fit-content;
  position: sticky;
  top: 80px;
  box-shadow: 0 3px 12px rgba(13,38,76,.06);
}
.picker-label{
  font-size:.7rem; text-transform:uppercase; letter-spacing:.5px;
  color:#6b7a86; font-weight:700; padding:4px 6px 8px;
}
/* Rich dropdown — stacked-layout alternative to .picker-list. Hidden in the
   2-column layout (the list shows there instead); revealed at ≤1100px. */
/* z-index here (with position:relative) lifts the whole dropdown — including
   the open menu — above the deep-dive column, which is a SIBLING in the grid.
   Without it, the deep-dive's positioned content (funnel bars, the within-reach
   pill) paints over the menu by DOM order. */
/* container-type so the chip-text rule below can key off the TRIGGER's own
   width, not the viewport — the trigger jumps wider when the sidebar drops at
   768px, and a viewport breakpoint would hide the chip when there's room. */
.pzdd-picker-dd{ display: none; position: relative; z-index: 100; container-type: inline-size; }
.pzdd-dd-trigger{
  display: flex; align-items: center; gap: 8px; width: 100%;
  background: #fff; border: 1px solid #ced4da; border-radius: 8px;
  padding: 8px 12px; cursor: pointer; text-align: left; font: inherit; color: #1a2a36;
  transition: border-color .15s ease, box-shadow .15s ease;
}
/* The trigger pill never wraps; its chip text is dropped (icon only) when the
   trigger is too narrow to fit it — handles the longest chip
   ("Dwell measured (no engagement breakdown)") without ever wrapping. */
.dd-trigger-pill{ white-space: nowrap; flex: 0 0 auto; }
@container (max-width: 500px){
  .dd-trigger-pill .pat-pill-text{ display: none; }
}
.pzdd-dd-trigger:hover{ border-color: #a4c2e3; }
.pzdd-dd-trigger:focus-visible{ outline: 0; border-color: #a4c2e3; box-shadow: 0 0 0 3px rgba(79,126,179,.18); }
.pzdd-dd-zone{
  flex: 1 1 auto; min-width: 0;
  font-weight: 600;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.pzdd-dd-eur{ font-weight: 700; color: #425461; font-variant-numeric: tabular-nums; flex: 0 0 auto; }
.pzdd-dd-caret{ color: #6b7a86; flex: 0 0 auto; transition: transform .15s ease; }
.pzdd-dd-caret.up{ transform: rotate(180deg); }
.pzdd-dd-backdrop{ position: fixed; inset: 0; z-index: 40; }
.pzdd-dd-menu{
  position: absolute; top: calc(100% + 4px); left: 0; right: 0; z-index: 50;
  list-style: none; margin: 0; padding: 6px;
  background: #fff; border: 1px solid #e6edf3; border-radius: 10px;
  box-shadow: 0 10px 28px rgba(13,38,76,.16);
  max-height: 60vh; overflow: auto;
  display: flex; flex-direction: column; gap: 4px;
}
.picker-list{
  list-style:none; padding:0; margin:0; display:flex; flex-direction:column; gap:4px;
}
.picker-item{
  cursor:pointer;
  padding:10px 12px;
  border-radius:8px;
  border:1px solid transparent;
  transition: background-color .15s ease, border-color .15s ease;
}
.picker-item:hover{ background:#f4f7fa; }
.picker-item.active{
  background:#eef4fb;
  border-color:#a4c2e3;
}
.picker-row{ display:flex; align-items:baseline; justify-content:space-between; gap:10px; }
.picker-zone{ font-weight:600; color:#1a2a36; }
.picker-eur{ font-size:.8rem; font-weight:700; color:#425461; font-variant-numeric: tabular-nums; }
.picker-meta{ margin-top:4px; }

.pat-pill{
  display:inline-flex; align-items:center; gap:4px;
  padding:2px 8px;
  border-radius:999px;
  font-size:.7rem; font-weight:700;
  letter-spacing:.2px;
  line-height:1;
}
.pat-pill.big{ font-size:.78rem; padding:4px 10px; }

/* ---------- Zone story column ---------- */
.pzdd-zone{
  display:flex; flex-direction:column; gap:22px;
}
.zone-head{
  display:flex; align-items:center; flex-wrap:wrap; gap:12px;
  padding-bottom:6px;
  border-bottom:1px solid #eef3f7;
}
.zone-head h3{ margin:0; color:#1a2a36; font-size:1.4rem; font-weight:700; }
.zone-stake{
  margin-left:auto;
  color:#425461;
  font-weight:500;
  font-size:.92rem;
  cursor: help;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.zone-stake > strong{ color:#2c6f58; font-weight:800; }
.zone-stake.muted{
  background: #f4f7fa;
  border-color: #d3dbe1;
  color: #6b7a86;
  font-weight: 500;
  cursor: default;
}
.stake-info{
  font-size: .85rem;
  opacity: .65;
}

.lever-pill{
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: .8rem;
  font-weight: 700;
  letter-spacing: .2px;
  white-space: nowrap;
}
.lever-pill i{ font-size: .78rem; }
.lever-pill.lever-eng{
  color: #4F7EB3;
  background: color-mix(in srgb, #4F7EB3 12%, #fff);
  border: 1px solid color-mix(in srgb, #4F7EB3 38%, #fff);
}
.lever-pill.lever-conv{
  color: #6F7C2A;
  background: color-mix(in srgb, #B2C149 18%, #fff);
  border: 1px solid color-mix(in srgb, #B2C149 55%, #fff);
}

/* Inline lever clause inside the within-reach sentence. Same color
   semantics as .lever-pill (blue for engagement, olive for conversion)
   but no chip background — reads as part of the sentence. */
.lever-clause{
  font-weight: 700;
  white-space: nowrap;
}
.lever-clause.lever-eng{ color: #4F7EB3; }
.lever-clause.lever-conv{ color: #6F7C2A; }

/* ---------- Dwell callout (tripwire-only zones) ---------- */
.zone-dwell-callout{
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 8px 0 16px;
  padding: 10px 14px;
  background: #eef3f8;
  border: 1px solid #d1dde9;
  border-radius: 10px;
  font-size: .92rem;
  color: #37526b;
}
.zone-dwell-callout i{ color:#4F7EB3; }
.dwell-callout-label{ color:#6b7a86; }
.dwell-callout-value{ font-weight:700; color:#1a2a36; font-size:1.05rem; }
.zone-dwell-callout .conf-chip{
  margin-left:auto;
  font-size:.72rem;
  padding:2px 8px;
  border-radius:10px;
}
.zone-dwell-callout .conf-chip.conf-high{   color:#2f6f3a; background:#f4faf5; border:1px solid #bfe2c7; }
.zone-dwell-callout .conf-chip.conf-medium{ color:#9a6b00; background:#fff8e6; border:1px solid #ffd97a; }
.zone-dwell-callout .conf-chip.conf-low{    color:#8a3a2a; background:#fdecea; border:1px solid #f3b3a8; }

/* ---------- Funnel ---------- */
.zone-funnel{
  background:#fff;
  border:1px solid #e6edf3;
  border-radius:12px;
  padding:18px 20px;
  box-shadow: 0 3px 12px rgba(13,38,76,.06);
}
.funnel-title{
  display:flex; align-items:center; gap:8px;
  font-weight:700; color:#1a2a36;
  margin-bottom:10px;
}
.funnel-title i{ color:#36910d; }

.zone-narrative{
  display:flex; align-items:flex-start; gap:8px;
  margin: 0 0 16px;
  padding: 10px 12px;
  background: #fff8e6;
  border: 1px solid #ffd97a;
  border-radius: 8px;
  color: #6a4f00;
  font-size: .92rem;
  line-height: 1.4;
}
.zone-narrative i{ font-size:1rem; color:#9a6b00; margin-top:2px; flex-shrink:0; }
.zone-narrative strong{ color:#1a2a36; }

.funnel-row{ margin-bottom: 16px; }
.funnel-row:last-child{ margin-bottom: 0; }
.funnel-row-head{
  display:flex; justify-content:space-between; align-items:baseline;
  margin-bottom:4px;
}
.funnel-label{ color:#425461; font-weight:600; }
.funnel-count{ color:#1a2a36; font-weight:700; font-variant-numeric:tabular-nums; }
.funnel-bar-wrap{
  position:relative;
  height: 22px;
  background:#eef3f7;
  border-radius:6px;
  overflow:hidden;
  display:flex; align-items:center;
}
.funnel-bar{
  position:absolute; left:0; top:0; bottom:0;
  border-radius:6px;
  transition: width .35s ease;
  opacity:.85;
}
.funnel-bar.simulated{
  background: transparent !important;
  border:2px dashed currentColor;
  opacity:.9;
}
.funnel-pct{
  position:relative;
  margin-left:10px;
  z-index:1;
  color:#1a2a36;
  font-weight:600;
  font-size:.85rem;
  font-variant-numeric:tabular-nums;
  white-space:nowrap;
}
.funnel-pct-sub{
  margin-left:4px;
  color:#6b7a86;
  font-weight:500;
  font-size:.75rem;
}
.funnel-drop{
  margin-top:4px;
  font-size:.76rem;
  color:#8a97a3;
  font-weight:500;
}

/* ---------- Simulator ---------- */
.zone-sim{
  background:#fff;
  border:1px solid #e6edf3;
  border-radius:12px;
  padding:18px 20px;
  box-shadow: 0 3px 12px rgba(13,38,76,.06);
  display:flex; flex-direction:column; gap:14px;
}
.sim-head{
  display:flex; align-items:center; gap:8px;
  font-weight:700; color:#1a2a36;
}
.sim-head i{ color:#4F7EB3; }
.sim-reset{
  margin-left:auto;
  background:transparent;
  border:1px solid #d3dbe1;
  border-radius:999px;
  padding:2px 10px;
  font-size:.78rem;
  color:#425461;
  cursor:pointer;
}
.sim-reset:hover{ background:#f4f7fa; }
.sim-grid{
  display:grid;
  /* auto-fit, not a fixed 1fr 1fr: the sliders stack the moment THEIR column
     (the deep-dive, narrower than the viewport when the picker is beside it)
     drops below ~two-up width — a viewport media query couldn't see that. */
  grid-template-columns: repeat(auto-fit, minmax(min(280px, 100%), 1fr));
  gap:16px;
}
.sim-grid > *{ min-width: 0; }   /* columns shrink instead of forcing width */
.sim-row label{
  display:flex; justify-content:space-between; align-items:baseline;
  font-size:.85rem; color:#425461; font-weight:600;
  margin-bottom:6px;
}
.sim-row label strong{ color:#1a2a36; }
.sim-row input[type="range"]{
  width:100%;
  accent-color:#4F7EB3;
}
.sim-row small{ display:block; color:#6b7a86; font-size:.78rem; margin-top:4px; }
.sim-result{
  display:flex; align-items:center; gap:10px;
  padding:10px 14px;
  border-radius:10px;
  background:#f4f7fa;
  border:1px solid #e6edf3;
  color:#425461;
}
.sim-result.positive{
  background: color-mix(in srgb, #36910d 10%, #fff);
  border-color: color-mix(in srgb, #36910d 35%, #e6edf3);
  color:#1a2a36;
}
.zone-sim.disabled{
  opacity: .9;
}
.sim-disabled-note{
  display:flex; align-items:center; gap:8px;
  padding:10px 14px;
  background:#f4f7fa;
  border:1px dashed #d3dbe1;
  border-radius:10px;
  color:#6b7a86;
  font-size:.88rem;
}
.sim-disabled-note i{ color:#D3A741; }
.sim-result i{ font-size:1.1rem; color:#36910d; }
.sim-result-text strong{
  font-size:1.05rem;
  color:#1a2a36;
}
.sim-result-text small{
  display:block;
  color:#6b7a86;
  font-size:.82rem;
  margin-top:2px;
}

/* ---------- Insights ---------- */
.zone-insights{
  background:#fff;
  border:1px solid #e6edf3;
  border-radius:12px;
  padding:18px 20px;
  box-shadow: 0 3px 12px rgba(13,38,76,.06);
}
/* Strong-state "Performing well" variant: green-tinted background +
   border so the panel reads as good-news rather than the regular
   needs-attention amber Lightbulb style. */
.zone-insights-strong{
  background: linear-gradient(180deg, #f5fbf6 0%, #ffffff 70%);
  border-color: #cfe8d2;
}
.zone-insights-strong .ins-head{ color: #1f6b3a; }
.zone-insights-strong .ins-head i{ color: #2c9c4a; }
.zone-insights-strong .ins-changed i{ color: #2c9c4a; }

.ins-head{
  display:flex; align-items:center; gap:8px;
  font-weight:700; color:#1a2a36;
  margin-bottom:12px;
}
.ins-head i{ color:#D3A741; }
.ins-chips{
  display:flex; flex-wrap:wrap; gap:6px;
  margin-bottom:14px;
}
.ins-chip{
  display:inline-flex; align-items:center;
  padding:3px 10px;
  border-radius:999px;
  background:#eef4fb;
  border:1px solid #d6e1ed;
  color:#355b8c;
  font-size:.75rem;
  font-weight:700;
}
.ins-chip.subtle{
  background:#f4f7fa;
  border-color:#e6edf3;
  color:#425461;
  font-weight:600;
}
.ins-chip.trend-up{ color:#2f6f3a; background:#F4FAF5; border-color:#bfe2c7; }
.ins-chip.trend-down{ color:#9a2727; background:#fff0f0; border-color:#f1c0c0; }
.ins-chip.trend-flat{ color:#425461; }

.ins-changed{
  display:flex; align-items:flex-start; gap:8px;
  margin: 0 0 12px;
  padding: 8px 12px;
  background: #f4f7fa;
  border-left: 3px solid #4F7EB3;
  border-radius: 6px;
  color: #1a2a36;
  font-size: .88rem;
  line-height: 1.4;
}
.ins-changed i{ color:#4F7EB3; margin-top:2px; flex-shrink:0; }
.ins-changed strong{ color:#1a2a36; }

.ins-actions{
  list-style:none; padding:0; margin:0;
  display:flex; flex-direction:column; gap:6px;
}
.ins-actions li{
  display:flex; align-items:flex-start; gap:8px;
  color:#1a2a36;
  font-size:.92rem;
}
.ins-actions i{ color:#36910d; margin-top:3px; flex-shrink:0; }
.ins-explainer{
  margin-top:12px;
  font-size:.82rem;
  color:#6b7a86;
  font-style:italic;
}
</style>
